import { useMemo, useState, type FormEvent } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTicketDraft } from './useTicketDraft';
import { useNavigate } from 'react-router-dom';
import { Send } from 'lucide-react';
import {
  ECOSYSTEM_SCOPE_LABEL,
  OTHER_SCOPE_LABEL,
  TICKET_CATEGORY_LABELS,
  TICKET_CATEGORY_VALUES,
  TICKET_PRIORITY_LABELS,
  TICKET_PRIORITY_VALUES,
  TICKET_SCOPE,
} from '@electrodavisan/shared';
import { ROUTES } from '../../app/config';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  PageHeader,
  Select,
  Textarea,
  useToast,
} from '../../components/ui';
import { ApiError } from '../../services/api';
import { useMyApplications } from '../../services/applications.service';
import { useCreateTicket } from '../../services/tickets.service';

const STEPS_PLACEHOLDER = `1. Ingrese a Clientes.
2. Presione Nuevo cliente.
3. Complete el formulario.
4. Presione Guardar.
5. Aparecio el error.`;

interface FormState {
  target: string;
  subject: string;
  category: string;
  description: string;
  actionPerformed: string;
  stepsToReproduce: string;
  errorMessage: string;
  expectedResult: string;
  actualResult: string;
  priority: string;
}

const INITIAL_STATE: FormState = {
  target: TICKET_SCOPE.ECOSYSTEM,
  subject: '',
  category: 'BUG',
  description: '',
  actionPerformed: '',
  stepsToReproduce: '',
  errorMessage: '',
  expectedResult: '',
  actualResult: '',
  priority: 'NORMAL',
};

export function NewTicketPage(): JSX.Element {
  const auth = useAuth();
  const identity = auth.user?.profile;
  if (!identity?.sub) return <p>Inicia sesión para crear un ticket.</p>;
  const draftKey = `ticket-draft:v1:${encodeURIComponent(identity.iss ?? '')}:${encodeURIComponent(identity.sub)}`;
  return <TicketForm key={draftKey} draftKey={draftKey} />;
}

function TicketForm({ draftKey }: { draftKey: string }): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: applications } = useMyApplications();
  const draft = useTicketDraft<FormState>(draftKey, INITIAL_STATE);
  const { values, setValues } = draft;
  const createTicket = useCreateTicket(() => draft.confirm(values));
  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * El desplegable siempre ofrece "ED Ecosystem" y "Otro / General",
   * de modo que el modulo de soporte es util incluso con cero aplicaciones
   * registradas.
   */
  const targetOptions = useMemo(
    () => [
      { value: TICKET_SCOPE.ECOSYSTEM, label: ECOSYSTEM_SCOPE_LABEL },
      ...(applications ?? []).map((application) => ({
        value: `app:${application.id}`,
        label: application.name,
      })),
      { value: TICKET_SCOPE.OTHER, label: OTHER_SCOPE_LABEL },
    ],
    [applications],
  );

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setValues({ ...values, [key]: value });
    setErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (values.subject.trim().length < 5) {
      next.subject = 'El asunto debe tener al menos 5 caracteres.';
    }
    if (values.description.trim().length < 10) {
      next.description = 'Describe el problema con al menos 10 caracteres.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (!validate()) return;

    const isApplication = values.target.startsWith('app:');

    createTicket.mutate(
      {
        scope: isApplication ? TICKET_SCOPE.APPLICATION : values.target,
        applicationId: isApplication ? values.target.slice(4) : null,
        subject: values.subject.trim(),
        category: values.category,
        description: values.description.trim(),
        actionPerformed: values.actionPerformed.trim() || null,
        stepsToReproduce: values.stepsToReproduce.trim() || null,
        errorMessage: values.errorMessage.trim() || null,
        expectedResult: values.expectedResult.trim() || null,
        actualResult: values.actualResult.trim() || null,
        priority: values.priority,
        // Contexto tecnico: el servidor completa navegador, SO y fecha.
        originUrl: window.location.href,
      },
      {
        onSuccess: (ticket) => {
          toast.success('Ticket creado', `Numero ${ticket.ticketNumber}`);
          navigate(ROUTES.ticketDetail(ticket.id));
        },
        onError: (error) => {
          const message =
            error instanceof ApiError ? error.message : 'No se pudo crear el ticket.';
          toast.error('No se pudo crear el ticket', message);
        },
      },
    );
  };

  return (
    <>
      <PageHeader
        title="Crear ticket de soporte"
        description="Cuantos mas detalles aportes, mas rapido podremos ayudarte."
      />

      <div role="status" className="mb-4 space-y-2 text-sm">
        {draft.recoverable ? <>
          <p>Hay un borrador de esta cuenta guardado en este navegador.</p>
          <Button onClick={draft.recover}>Recuperar borrador</Button>
          <Button variant="secondary" onClick={draft.discard}>Descartar borrador</Button>
        </> : <p>{draft.failed
          ? 'No se pudo guardar el borrador en este navegador. No cierres esta página hasta enviarlo o copiar su contenido.'
          : 'El borrador se conserva en este navegador al salir. Solo se elimina al confirmar el envío o descartarlo.'}</p>}
      </div>
      <fieldset disabled={Boolean(draft.recoverable) || createTicket.isPending}>
      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        {/* ------------------------------------------ informacion basica */}
        <Card>
          <CardHeader
            title="Informacion basica"
            description="Donde ocurre el problema y de que tipo es."
          />
          <CardBody className="grid gap-5 sm:grid-cols-2">
            <Select
              label="Aplicacion afectada"
              required
              value={values.target}
              onChange={(event) => set('target', event.target.value)}
              options={targetOptions}
              hint="Si no ves la aplicacion en la lista, elige Otro / General."
            />

            <Select
              label="Categoria"
              required
              value={values.category}
              onChange={(event) => set('category', event.target.value)}
              options={TICKET_CATEGORY_VALUES.map((category) => ({
                value: category,
                label: TICKET_CATEGORY_LABELS[category],
              }))}
            />

            <Input
              label="Asunto"
              required
              containerClassName="sm:col-span-2"
              value={values.subject}
              onChange={(event) => set('subject', event.target.value)}
              error={errors.subject}
              placeholder="Resume el problema en una frase"
              maxLength={200}
            />
          </CardBody>
        </Card>

        {/* -------------------------------------------- que ha ocurrido */}
        <Card>
          <CardHeader
            title="Descripcion del problema"
            description="Cuentanos con tus palabras que paso."
          />
          <CardBody className="space-y-5">
            <Textarea
              label="Describe que ocurrio"
              required
              value={values.description}
              onChange={(event) => set('description', event.target.value)}
              error={errors.description}
              hint="Explicanos que estabas intentando hacer y que sucedio."
              rows={6}
              maxLength={10_000}
            />

            <Input
              label="Que estabas haciendo cuando ocurrio"
              value={values.actionPerformed}
              onChange={(event) => set('actionPerformed', event.target.value)}
              placeholder="Estaba intentando guardar un nuevo cliente."
              maxLength={2_000}
            />

            <Textarea
              label="Pasos para reproducir"
              value={values.stepsToReproduce}
              onChange={(event) => set('stepsToReproduce', event.target.value)}
              placeholder={STEPS_PLACEHOLDER}
              hint="Numera los pasos, uno por linea."
              rows={6}
              maxLength={5_000}
            />
          </CardBody>
        </Card>

        {/* ----------------------------------------------- detalle tecnico */}
        <Card>
          <CardHeader
            title="Detalle tecnico"
            description="Opcional, pero acelera mucho el diagnostico."
          />
          <CardBody className="space-y-5">
            <Textarea
              label="Mensaje de error"
              monospace
              value={values.errorMessage}
              onChange={(event) => set('errorMessage', event.target.value)}
              hint="Si la aplicacion mostro un mensaje de error, copialo y pegalo exactamente aqui."
              placeholder="Pega aqui el mensaje completo o el stack trace"
              rows={6}
              maxLength={20_000}
            />

            <div className="grid gap-5 sm:grid-cols-2">
              <Textarea
                label="Que esperabas que sucediera"
                value={values.expectedResult}
                onChange={(event) => set('expectedResult', event.target.value)}
                rows={3}
                maxLength={2_000}
              />
              <Textarea
                label="Que ocurrio realmente"
                value={values.actualResult}
                onChange={(event) => set('actualResult', event.target.value)}
                rows={3}
                maxLength={2_000}
              />
            </div>

            <Select
              label="Prioridad percibida"
              value={values.priority}
              onChange={(event) => set('priority', event.target.value)}
              options={TICKET_PRIORITY_VALUES.map((priority) => ({
                value: priority,
                label: TICKET_PRIORITY_LABELS[priority],
              }))}
              hint="Es tu percepcion del impacto. El equipo de soporte revisara y ajustara la prioridad real."
              containerClassName="sm:max-w-xs"
            />
          </CardBody>
        </Card>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={() => {
            if (!draft.failed || window.confirm("El borrador no se pudo guardar. ¿Salir y perder los cambios?")) navigate(ROUTES.tickets);
          }}>
            Volver conservando borrador
          </Button>
          <Button
            type="submit"
            loading={createTicket.isPending}
            leftIcon={<Send className="h-4 w-4" aria-hidden />}
          >
            Enviar ticket
          </Button>
        </div>

        <p className="text-[13px] text-text-muted">
          Al enviar el ticket se registran automaticamente tu usuario, la fecha, el navegador y
          el sistema operativo aproximado para ayudar al diagnostico. Podras adjuntar archivos
          desde el detalle del ticket.
        </p>
      </form>
      </fieldset>
    </>
  );
}
